/**
 * Debug Logging Utility with Log Level Control
 *
 * This utility provides console logging with configurable log levels via PostHog feature flags.
 * Follows industry-standard log level hierarchy where higher numbers = less output.
 *
 * **Log Levels**:
 * - TRACE (0): Most verbose - execution flow, call stacks
 * - DEBUG (1): Diagnostic information for developers
 * - INFO (2): General informational messages [DEFAULT]
 * - WARN (3): Warnings - always displays regardless of level
 * - ERROR (4): Errors - always displays regardless of level
 * - OFF (5): Disable trace, debug, and info logging
 *
 * **Key Features**:
 * - Preserves original call stack in browser DevTools
 * - Synchronous operation (no async overhead)
 * - warn() and error() always display for critical diagnostics
 * - trace(), debug(), info() controlled by log level threshold
 *
 * @example
 * ```typescript
 * import { trace, debug, info, warn, error } from '@client/utils/debug';
 *
 * trace('Execution path:', path);    // Only at TRACE level
 * debug('User data:', user);          // At DEBUG level or lower
 * info('Service started');            // At INFO level or lower
 * warn('Deprecated API');             // Always displays
 * error('Operation failed:', err);    // Always displays
 * ```
 *
 * @example Tagged logging
 * ```typescript
 * import { createLogger } from '@client/utils/debug';
 *
 * const log = createLogger('AuthService');
 * log.debug('Processing login');      // [AuthService] Processing login
 * log.error('Auth failed:', error);   // [AuthService] Auth failed: ...
 * ```
 *
 * @module debug
 */

import { getFlagSync } from '@client/utils/feature-flags';

/**
 * Standard log levels following industry conventions
 * Higher values = less output (more restrictive)
 */
export const LogLevel = {
    /** Most verbose - execution flow, function entry/exit, call stacks */
    TRACE: 0,
    /** Diagnostic information for developers - variable values, state */
    DEBUG: 1,
    /** General informational messages - service lifecycle, configuration */
    INFO: 2,
    /** Warnings - always displays, not controlled by level */
    WARN: 3,
    /** Errors - always displays, not controlled by level */
    ERROR: 4,
    /** Disable trace, debug, and info logging (warn/error still show) */
    OFF: 5,
} as const;

export type LogLevel = (typeof LogLevel)[keyof typeof LogLevel];

/**
 * Reverse mapping for display purposes
 */
export const LogLevelName: Record<number, string> = {
    0: 'TRACE',
    1: 'DEBUG',
    2: 'INFO',
    3: 'WARN',
    4: 'ERROR',
    5: 'OFF',
};

/**
 * Mapping from string log level names to numeric values
 * Handles PostHog returning string variants instead of numbers
 */
const LogLevelFromString: Record<string, LogLevel> = {
    trace: LogLevel.TRACE,
    debug: LogLevel.DEBUG,
    info: LogLevel.INFO,
    warn: LogLevel.WARN,
    error: LogLevel.ERROR,
    off: LogLevel.OFF,
};

/**
 * PostHog feature flag key for log level control
 */
const LOG_LEVEL_FLAG_KEY = 'web-console-log-level' as const;

/**
 * Default log level when flag is not set
 */
const DEFAULT_LOG_LEVEL = LogLevel.INFO;

/**
 * Pre-bound console methods for call stack preservation
 *
 * Using .bind() ensures the browser DevTools shows the original caller location
 * in the call stack, not this debug utility file.
 */
const _trace = console.trace.bind(console);
const _log = console.log.bind(console);
const _info = console.info.bind(console);
const _warn = console.warn.bind(console);
const _error = console.error.bind(console);

/**
 * Get the current log level from PostHog feature flags
 *
 * Normalises the flag value to a numeric LogLevel, handling both:
 * - Numeric values (e.g., 1) - returned as-is
 * - String values (e.g., "debug") - converted to numeric equivalent
 *
 * @returns Current log level (0-5)
 */
function getCurrentLogLevel(): LogLevel {
    // PostHog can return either numeric values (e.g., 1) or string values (e.g., "debug")
    // Use a union type to handle both cases
    const value = getFlagSync<number | string>(LOG_LEVEL_FLAG_KEY, DEFAULT_LOG_LEVEL);

    // If it's already a valid numeric LogLevel, return it
    if (typeof value === 'number' && value >= 0 && value <= 5) {
        return value as LogLevel;
    }

    // If it's a string, convert to numeric
    if (typeof value === 'string') {
        const normalised = value.toLowerCase();
        if (normalised in LogLevelFromString) {
            return LogLevelFromString[normalised];
        }
    }

    // Fallback to default
    return DEFAULT_LOG_LEVEL;
}

/**
 * Check if a message at the given level should be logged
 *
 * Messages are logged if their level is >= current threshold.
 * Example: If level is INFO (2), then INFO (2), WARN (3), ERROR (4) display.
 *
 * @param messageLevel - The level of the message to log
 * @returns true if the message should be logged
 */
function shouldLog(messageLevel: LogLevel): boolean {
    const currentLevel = getCurrentLogLevel();
    return messageLevel >= currentLevel;
}

/**
 * Trace logging - most verbose level
 *
 * Logs with console.trace to show full call stack. Only displays when log level
 * is set to TRACE (0).
 *
 * Use for: Execution flow tracking, debugging complex call chains
 *
 * @param args - Arguments to log (same as console.trace)
 *
 * @example
 * ```typescript
 * trace('Function called from:', functionName);
 * trace('Execution path:', { step: 1, state: currentState });
 * ```
 */
export const trace = (...args: unknown[]): void => {
    if (shouldLog(LogLevel.TRACE)) {
        _trace(...args);
    }
};

/**
 * Debug logging - diagnostic level
 *
 * Logs with console.log. Displays when log level is DEBUG (1) or lower.
 *
 * Use for: Diagnostic information, variable values, intermediate states
 *
 * @param args - Arguments to log (same as console.log)
 *
 * @example
 * ```typescript
 * debug('User data:', user);
 * debug('Processing item:', itemId, item);
 * ```
 */
export const debug = (...args: unknown[]): void => {
    if (shouldLog(LogLevel.DEBUG)) {
        _log(...args);
    }
};

/**
 * Info logging - informational level
 *
 * Logs with console.info. Displays when log level is INFO (2) or lower.
 *
 * Use for: General information, service lifecycle, configuration, normal operations
 *
 * @param args - Arguments to log (same as console.info)
 *
 * @example
 * ```typescript
 * info('Service initialized:', serviceName);
 * info('Configuration loaded:', config);
 * ```
 */
export const info = (...args: unknown[]): void => {
    if (shouldLog(LogLevel.INFO)) {
        _info(...args);
    }
};

/**
 * Warning logging - always displays
 *
 * Logs with console.warn. Always displays regardless of log level setting.
 * Use for important diagnostic warnings that should always be visible.
 *
 * Note: For critical system warnings, consider using console.warn directly
 * and tracking with Sentry.
 *
 * Use for: Deprecation warnings, unexpected conditions, non-critical issues
 *
 * @param args - Arguments to log (same as console.warn)
 *
 * @example
 * ```typescript
 * warn('Deprecated API used:', legacyFunction);
 * warn('Slow operation detected:', duration);
 * ```
 */
export const warn = (...args: unknown[]): void => {
    _warn(...args);
};

/**
 * Error logging - always displays
 *
 * Logs with console.error. Always displays regardless of log level setting.
 * Use for important diagnostic errors that should always be visible.
 *
 * Note: For critical system errors, use console.error directly and
 * track with Sentry.captureException().
 *
 * Use for: Operation failures, recoverable errors, validation failures
 *
 * @param args - Arguments to log (same as console.error)
 *
 * @example
 * ```typescript
 * error('Operation failed:', error);
 * error('Validation error:', { field, message });
 * ```
 */
export const error = (...args: unknown[]): void => {
    _error(...args);
};

/**
 * Check if logging is enabled at the specified level
 *
 * Useful for conditionally executing expensive operations that generate debug data.
 *
 * @param level - The log level to check
 * @returns true if logging is enabled at or below the specified level
 *
 * @example
 * ```typescript
 * if (isLogLevelEnabled(LogLevel.DEBUG)) {
 *     const expensiveData = computeExpensiveDebugInfo();
 *     debug('Debug info:', expensiveData);
 * }
 * ```
 */
export const isLogLevelEnabled = (level: LogLevel): boolean => {
    return shouldLog(level);
};

/**
 * Get the current log level setting
 *
 * @returns Current log level (0-5)
 *
 * @example
 * ```typescript
 * const level = getLogLevel();
 * console.log('Current level:', LogLevelName[level]);
 * ```
 */
export const getLogLevel = (): LogLevel => {
    return getCurrentLogLevel();
};

/**
 * Logger interface returned by createLogger
 */
export interface Logger {
    trace: (...args: unknown[]) => void;
    debug: (...args: unknown[]) => void;
    info: (...args: unknown[]) => void;
    warn: (...args: unknown[]) => void;
    error: (...args: unknown[]) => void;
}

/**
 * Create a tagged logger for a specific module or component
 *
 * Automatically prefixes all messages with the provided tag for easier filtering
 * and identification in browser DevTools console.
 *
 * All methods respect their respective log level settings (except warn/error which
 * always display).
 *
 * @param tag - Tag to prefix all log messages (e.g., 'AuthService', 'UserModule')
 * @returns Logger object with trace, debug, info, warn, error methods
 *
 * @example
 * ```typescript
 * const log = createLogger('AuthService');
 *
 * log.trace('Function entry');          // [AuthService] Function entry
 * log.debug('User authenticated');      // [AuthService] User authenticated
 * log.info('Token refreshed');          // [AuthService] Token refreshed
 * log.warn('Token expiring soon');      // [AuthService] Token expiring soon (always)
 * log.error('Auth failed:', error);     // [AuthService] Auth failed: ... (always)
 * ```
 *
 * @example In a class
 * ```typescript
 * class UserService {
 *     private log = createLogger('UserService');
 *
 *     login(email: string) {
 *         this.log.debug('Login attempt:', email);
 *         // ...
 *     }
 * }
 * ```
 */
export const createLogger = (tag: string): Logger => {
    const formattedTag = `[${tag}]`;

    return {
        trace: (...args: unknown[]): void => {
            if (shouldLog(LogLevel.TRACE)) {
                _trace(formattedTag, ...args);
            }
        },
        debug: (...args: unknown[]): void => {
            if (shouldLog(LogLevel.DEBUG)) {
                _log(formattedTag, ...args);
            }
        },
        info: (...args: unknown[]): void => {
            if (shouldLog(LogLevel.INFO)) {
                _info(formattedTag, ...args);
            }
        },
        warn: (...args: unknown[]): void => {
            _warn(formattedTag, ...args);
        },
        error: (...args: unknown[]): void => {
            _error(formattedTag, ...args);
        },
    };
};

/**
 * Wrap a function to automatically log entry and exit
 *
 * Logs function calls and completions at DEBUG level. Logs errors at ERROR level
 * (always displays). Preserves function return value and re-throws errors.
 *
 * @param fn - Function to wrap with logging
 * @param name - Optional name for the function (defaults to fn.name)
 * @returns Wrapped function with logging
 *
 * @example
 * ```typescript
 * const processData = wrapFunction(
 *     (data: Data[]) => data.map(transform),
 *     'processData'
 * );
 *
 * processData(items);
 * // Logs (if DEBUG enabled):
 * // [wrapFunction] processData called with args: [...]
 * // [wrapFunction] processData completed
 * ```
 */
export const wrapFunction = <T extends (...args: unknown[]) => unknown>(fn: T, name?: string): T => {
    const functionName = name || fn.name || 'anonymous';

    return ((...args: unknown[]) => {
        if (shouldLog(LogLevel.DEBUG)) {
            _log(`[wrapFunction] ${functionName} called with args:`, args);
        }

        try {
            const result = fn(...args);

            if (shouldLog(LogLevel.DEBUG)) {
                _log(`[wrapFunction] ${functionName} completed`);
            }

            return result;
        } catch (err) {
            // Error always displays
            _error(`[wrapFunction] ${functionName} threw error:`, err);
            throw err;
        }
    }) as T;
};

/**
 * Create a timer for measuring performance
 *
 * Returns a function that, when called, logs the elapsed time at DEBUG level.
 * Useful for debugging performance issues.
 *
 * @param label - Label for the timer
 * @returns Function that logs elapsed time when called
 *
 * @example
 * ```typescript
 * const timer = createTimer('Data Processing');
 *
 * processData(items);
 *
 * timer(); // Logs (if DEBUG enabled): Data Processing: 1234.56ms
 * ```
 */
export const createTimer = (label: string) => {
    const startTime = performance.now();

    return (): void => {
        if (shouldLog(LogLevel.DEBUG)) {
            const elapsed = performance.now() - startTime;
            _log(`${label}: ${elapsed.toFixed(2)}ms`);
        }
    };
};
